import { FIELD_REGISTRY, REPORT_DATA_SOURCES } from './fieldRegistry';

// Banded Report Engine — FastReport / Crystal Reports tarzı yazdırma motoru.
// İçerikler:
//  - Layout schema (Adım 1 v2)
//  - Expression evaluator (alan referansları + Sum/Count/Avg/Min/Max + aritmetik + IIF)
//  - HTML render (yazdırma) — band sırası ve master-detail / group ile
//
// Layout schema:
// {
//   paper_size: 'A4'|'A3'|'Letter'|'Legal',
//   page_orientation: 'portrait'|'landscape',
//   margin_top, margin_right, margin_bottom, margin_left  (mm)
//
//   data_sources: {
//     main:   { fields: [{key, label, type}] },
//     detail: { master_key, detail_key, fields: [{key, label, type}] }   // opsiyonel
//   }
//
//   group: { by_field, sort_dir: 'asc'|'desc' }   // opsiyonel
//
//   bands: {
//     report_header: { height, visible },
//     page_header:   { height, visible },
//     group_header:  { height, visible },
//     master:        { height, visible },
//     detail:        { height, visible, source: 'detail' },
//     group_footer:  { height, visible },
//     page_footer:   { height, visible },
//     report_footer: { height, visible }
//   }
//
//   elements: [
//     {
//       id, band: 'report_header'|'page_header'|...,
//       type: 'text'|'field'|'expression'|'image'|'line'|'rect'|'barcode',
//       x, y, w, h,                  (mm, band içine göreli)
//       rotation: 0|90|180|270|number (derece)
//       data_source: 'main'|'detail'|'context'
//       field: 'name',               (type=field)
//       expression: 'Sum([qty])',    (type=expression)
//       text: 'Sabit yazı',          (type=text)
//       font_family, font_size, bold, italic, color, align, valign,
//       background, border_color, border_width,
//       hide_if_zero, page_break_before, page_break_after,
//       conditional: [{ when_expr: 'value < 0', color: '#dc2626', bold: true }]
//     }
//   ]
// }

export const PAPER_SIZES = {
  A4:     { w: 210,   h: 297 },
  A3:     { w: 297,   h: 420 },
  Letter: { w: 215.9, h: 279.4 },
  Legal:  { w: 215.9, h: 355.6 },
};

export const BANDS = ['report_header', 'page_header', 'group_header', 'master', 'detail', 'group_footer', 'page_footer', 'report_footer'];

export const BAND_LABELS = {
  report_header: 'Report Header',
  page_header:   'Page Header',
  group_header:  'Group Header',
  master:        'Master Data',
  detail:        'Detail Data',
  group_footer:  'Group Footer',
  page_footer:   'Page Footer',
  report_footer: 'Report Footer',
};

export const BAND_COLORS = {
  report_header: 'rgba(168,85,247,0.10)',
  page_header:   'rgba(59,130,246,0.10)',
  group_header:  'rgba(14,165,233,0.10)',
  master:        'rgba(16,185,129,0.08)',
  detail:        'rgba(34,197,94,0.06)',
  group_footer:  'rgba(14,165,233,0.10)',
  page_footer:   'rgba(245,158,11,0.10)',
  report_footer: 'rgba(168,85,247,0.10)',
};

export const FONT_OPTIONS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Calibri, sans-serif', label: 'Calibri' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Courier New", monospace', label: 'Courier New' },
];

// Yeni şablon oluştur. reportKey verilirse data_sources merkezi registry'den tohumlanır.
// Geriye dönük uyumluluk: reportKey yoksa eski stok-list varsayılanı.
export function createEmptyLayout(reportKey = 'stock_list') {
  let mainSource = { name: 'Ana Veri', source_type: null, fields: [] };
  let detailSource = { name: '', source_type: null, fields: [], master_key: 'id', detail_key: 'master_id' };
  const cfg = REPORT_DATA_SOURCES[reportKey] || REPORT_DATA_SOURCES.stock_list;
  if (cfg?.master && FIELD_REGISTRY[cfg.master]) {
    mainSource = {
      name: FIELD_REGISTRY[cfg.master].label,
      source_type: cfg.master,
      fields: FIELD_REGISTRY[cfg.master].fields.slice(),
    };
  }
  if (cfg?.detail && FIELD_REGISTRY[cfg.detail]) {
    detailSource = {
      name: FIELD_REGISTRY[cfg.detail].label,
      source_type: cfg.detail,
      fields: FIELD_REGISTRY[cfg.detail].fields.slice(),
      master_key: 'id',
      detail_key: 'master_id',
    };
  }

  return {
    paper_size: 'A4',
    page_orientation: 'portrait',
    margin_top: 12, margin_bottom: 12, margin_left: 12, margin_right: 12,
    data_sources: {
      main:   mainSource,
      detail: detailSource,
    },
    group: { by_field: '', sort_dir: 'asc' },
    bands: {
      report_header: { height: 0,  visible: false },
      page_header:   { height: 24, visible: true  },
      group_header:  { height: 0,  visible: false },
      master:        { height: 8,  visible: true  },
      detail:        { height: 0,  visible: false, source: 'detail' },
      group_footer:  { height: 0,  visible: false },
      page_footer:   { height: 12, visible: true  },
      report_footer: { height: 0,  visible: false },
    },
    elements: [],
    accent_color: '#3b82f6',
    font_family: 'Arial, sans-serif',
    font_size: 10,
  };
}

// ─── Expression Engine ───────────────────────────────────────────
// Desteklenen söz dizimi:
//   [field]                          → mevcut bağlamdaki alanın değeri
//   Sum([qty])                       → ana veri kümesi üzerinde toplam (band'a göre kapsam değişir)
//   Count() / Count([field])         → satır sayısı veya null olmayan alan sayısı
//   Avg([f]) / Min([f]) / Max([f])
//   IIF(cond, a, b)                  → koşullu
//   Aritmetik: + - * / ( )           → standart
//   String literal: "metin"
//   Number literal: 12.5
//
// Kapsam (scope):
//   - master/detail bandında çağrıldığında bağlam o satıra ait
//   - footer/header'da Sum tüm satırların üzerinden alınır
//   - group_footer'da Sum sadece o grubun satırlarının
//
// evaluateExpr(expr, ctx) → { value, error }
//   ctx: { current, rows, group_rows, detail_rows, sample }
export function evaluateExpr(expr, ctx) {
  if (expr == null) return { value: '' };
  let s = String(expr).trim();
  if (!s) return { value: '' };
  try {
    // Aggregate çağrılarını önce hesaplayıp literal değerlere çevir
    s = s.replace(/(Sum|Count|Avg|Min|Max)\s*\(\s*(\[[^\]]+\])?\s*\)/gi, (m, fn, fld) => {
      const key = fld ? fld.slice(1, -1) : null;
      const rows = ctx.rows || [];
      let n = 0, sum = 0, min = Infinity, max = -Infinity;
      for (const r of rows) {
        if (key) {
          const v = r?.[key];
          if (v == null || v === '') continue;
          const num = parseFloat(v);
          if (!isFinite(num)) continue;
          n++; sum += num;
          if (num < min) min = num;
          if (num > max) max = num;
        } else {
          n++;
        }
      }
      let res = 0;
      if (fn.toLowerCase() === 'sum')   res = sum;
      if (fn.toLowerCase() === 'count') res = n;
      if (fn.toLowerCase() === 'avg')   res = n ? (sum / n) : 0;
      if (fn.toLowerCase() === 'min')   res = n ? min : 0;
      if (fn.toLowerCase() === 'max')   res = n ? max : 0;
      return String(res);
    });

    // IIF(a, b, c) → ((a) ? (b) : (c))
    s = replaceIIF(s);

    // [field] → __ctx[..]
    s = s.replace(/\[([a-zA-Z_][\w\.]*)\]/g, (m, key) => {
      const v = ctx.current?.[key];
      if (typeof v === 'number') return String(v);
      if (v == null) return '""';
      // string ise çift tırnak içinde
      return JSON.stringify(String(v));
    });

    // Eval (sandbox: yalnız aritmetik + ternary izinli operatörler)
    // Güvensiz olduğu için sadece kullanıcı kendi şablonu için bu çalışır.
    // eslint-disable-next-line no-new-func
    const fn = new Function('return (' + s + ');');
    const value = fn();
    return { value };
  } catch (err) {
    return { value: '#ERR', error: err.message };
  }
}

// IIF(cond, a, b) parser — virgül seviyesini parantez içerisinde de doğru ayırır
function replaceIIF(s) {
  let out = ''; let i = 0;
  while (i < s.length) {
    const m = s.slice(i).match(/^IIF\s*\(/i);
    if (!m) { out += s[i++]; continue; }
    i += m[0].length;
    let depth = 1, parts = [''], pi = 0;
    while (i < s.length && depth > 0) {
      const c = s[i];
      if (c === '(') depth++;
      else if (c === ')') { depth--; if (depth === 0) { i++; break; } }
      if (depth === 1 && c === ',') { pi++; parts[pi] = ''; i++; continue; }
      parts[pi] += c; i++;
    }
    if (parts.length >= 3) out += '((' + replaceIIF(parts[0]) + ')?(' + replaceIIF(parts[1]) + '):(' + replaceIIF(parts[2]) + '))';
  }
  return out;
}

// ─── Print HTML render ───────────────────────────────────────────
// renderPrintHtml({ layout, name, mainRows, detailRowsByMaster?, context })
//   detailRowsByMaster: opsiyonel; { [master.id]: [...detailRows] }
//   context: { title, subtitle, company_name, filter_summary, footer_text }
export function renderPrintHtml({ layout, name, mainRows = [], detailRowsByMaster = {}, context = {} }) {
  if (!layout) return '';
  const dim = (layout.page_orientation === 'landscape')
    ? { w: PAPER_SIZES[layout.paper_size]?.h || 297, h: PAPER_SIZES[layout.paper_size]?.w || 210 }
    : { w: PAPER_SIZES[layout.paper_size]?.w || 210, h: PAPER_SIZES[layout.paper_size]?.h || 297 };
  const innerW = dim.w - layout.margin_left - layout.margin_right;
  const escape = (s) => String(s ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));

  // Element renderı (band içi göreli pozisyonu mutlak div olarak kurar)
  const renderElement = (el, scope) => {
    // hide_if_zero
    let raw = '';
    let valueForCheck = null;
    if (el.type === 'text') {
      raw = el.text || '';
    } else if (el.type === 'field') {
      const ds = el.data_source || 'main';
      const obj = ds === 'detail' ? scope.current_detail : (ds === 'context' ? scope.context : scope.current);
      const v = obj?.[el.field];
      valueForCheck = v;
      if (v == null) raw = '';
      else if (typeof v === 'number') raw = formatNumber(v, el.format);
      else raw = String(v);
    } else if (el.type === 'expression') {
      const exprCtx = buildExprCtx(el, scope);
      const r = evaluateExpr(el.expression || '', exprCtx);
      const v = r.value;
      valueForCheck = v;
      if (typeof v === 'number') raw = formatNumber(v, el.format);
      else raw = String(v ?? '');
    }

    if (el.hide_if_zero && (valueForCheck === 0 || valueForCheck === '0' || valueForCheck === '' || valueForCheck == null)) {
      return '';
    }

    // Conditional formatting
    let extraStyle = '';
    if (Array.isArray(el.conditional) && el.conditional.length && (el.type === 'field' || el.type === 'expression')) {
      const ctx = buildExprCtx(el, scope, { value: valueForCheck });
      for (const c of el.conditional) {
        const r = evaluateExpr(c.when_expr || 'false', ctx);
        if (r.value) {
          if (c.color)        extraStyle += `color:${c.color};`;
          if (c.background)   extraStyle += `background:${c.background};`;
          if (c.bold)         extraStyle += 'font-weight:700;';
          if (c.italic)       extraStyle += 'font-style:italic;';
        }
      }
    }

    const rotation = parseFloat(el.rotation) || 0;
    const w = el.w, h = el.h;
    const halign = el.align || 'left';
    const valign = el.valign || 'middle';
    const styleBase =
      `position:absolute;left:${el.x}mm;top:${el.y}mm;width:${w}mm;height:${h}mm;` +
      (rotation ? `transform:rotate(${rotation}deg);transform-origin:center center;` : '');

    if (el.type === 'image') {
      if (el.src) return `<div style="${styleBase}"><img src="${escape(el.src)}" style="width:100%;height:100%;object-fit:${el.fit || 'contain'};${extraStyle}"/></div>`;
      return '';
    }
    if (el.type === 'line') {
      const orient = (h > w) ? 'vertical' : 'horizontal';
      return `<div style="${styleBase}background:${el.color || '#000'};${orient === 'vertical' ? `width:${el.thickness || 1}px;` : `height:${el.thickness || 1}px;`}${extraStyle}"></div>`;
    }
    if (el.type === 'rect') {
      return `<div style="${styleBase}border:${el.border_width || 1}px solid ${el.border_color || el.color || '#000'};background:${el.background || el.fill || 'transparent'};box-sizing:border-box;${extraStyle}"></div>`;
    }
    if (el.type === 'barcode') {
      // Gerçek barkod inline SVG olarak render — JsBarcode ile yazdırma penceresinde basılır
      const barcodeId = 'bc_' + Math.random().toString(36).slice(2, 9);
      const value = raw || '0000';
      return `<svg id="${barcodeId}" data-bc-format="${el.format || 'CODE128'}" data-bc-value="${escape(value)}" data-bc-text="${el.show_text === false ? 'false' : 'true'}" style="${styleBase}width:${el.w}mm;height:${el.h}mm;${extraStyle}"></svg>`;
    }

    // text/field/expression
    const justify = halign === 'center' ? 'center' : halign === 'right' ? 'flex-end' : 'flex-start';
    const alignItems = valign === 'top' ? 'flex-start' : valign === 'bottom' ? 'flex-end' : 'center';
    const fontStyle =
      `font-family:${el.font_family || layout.font_family || 'Arial'};` +
      `font-size:${el.font_size || layout.font_size || 10}pt;` +
      `font-weight:${el.bold ? 700 : 400};` +
      `font-style:${el.italic ? 'italic' : 'normal'};` +
      `color:${el.color || '#000'};` +
      `text-align:${halign};` +
      `display:flex;align-items:${alignItems};justify-content:${justify};` +
      (el.background ? `background:${el.background};` : '') +
      (el.border_color && el.border_width ? `border:${el.border_width}px solid ${el.border_color};` : '') +
      `overflow:hidden;padding:1px 3px;box-sizing:border-box;line-height:1.2;white-space:pre-wrap;`;
    return `<div style="${styleBase}${fontStyle}${extraStyle}">${escape(raw)}</div>`;
  };

  const buildExprCtx = (el, scope, override) => ({
    current: el.data_source === 'detail' ? scope.current_detail : (el.data_source === 'context' ? scope.context : scope.current),
    rows: el.data_source === 'detail' ? scope.detail_rows : (scope.scope_rows || mainRows),
    detail_rows: scope.detail_rows || [],
    context: scope.context,
    ...override,
  });

  const elementsByBand = (band) => layout.elements.filter(e => e.band === band);

  // Band wrapper'ı — height'a göre alan açar, bant rengi/etiketsiz
  const wrapBand = (band, content, opts = {}) => {
    const cfg = layout.bands[band];
    if (!cfg || !cfg.visible || cfg.height <= 0) return '';
    const pageBreak = opts.page_break ? 'page-break-before:always;' : '';
    const fixedFooter = (band === 'page_footer') ? 'position:fixed;bottom:0;left:' + layout.margin_left + 'mm;right:' + layout.margin_right + 'mm;' : 'position:relative;';
    return `<div class="rpt-band rpt-band-${band}" style="${fixedFooter}height:${cfg.height}mm;${pageBreak}width:100%;">${content}</div>`;
  };

  // Master ve Detail/group rendering
  const groupBy = layout.group?.by_field;
  let sortedRows = [...mainRows];
  if (groupBy) {
    sortedRows.sort((a, b) => {
      const av = a?.[groupBy], bv = b?.[groupBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; if (bv == null) return -1;
      const cmp = (av < bv) ? -1 : (av > bv ? 1 : 0);
      return layout.group?.sort_dir === 'desc' ? -cmp : cmp;
    });
  }

  const groups = groupBy
    ? Object.entries(sortedRows.reduce((acc, r) => { const k = r[groupBy] ?? ''; (acc[k] = acc[k] || []).push(r); return acc; }, {}))
    : [['', sortedRows]];

  const groupBlocks = groups.map(([gkey, gRows]) => {
    const masterBlocks = gRows.map(row => {
      const detailRows = detailRowsByMaster[row.id] || [];
      const detail_visible = layout.bands.detail?.visible && detailRows.length > 0;
      const masterEls = elementsByBand('master');
      const detailEls = elementsByBand('detail');
      const masterBand = masterEls.length || layout.bands.master?.visible
        ? wrapBand('master', masterEls.map(el => renderElement(el, { current: row, context, scope_rows: mainRows })).join(''), { page_break: el => false })
        : '';
      const detailBand = detail_visible
        ? detailRows.map(d => wrapBand('detail',
            detailEls.map(el => renderElement(el, { current: row, current_detail: d, detail_rows: detailRows, context, scope_rows: detailRows })).join('')
          )).join('')
        : '';
      return masterBand + detailBand;
    }).join('');

    const ghEls = elementsByBand('group_header');
    const gfEls = elementsByBand('group_footer');
    const groupContext = { ...context, group_value: gkey };
    const ghBand = (groupBy && layout.bands.group_header?.visible)
      ? wrapBand('group_header', ghEls.map(el => renderElement(el, { current: { [groupBy]: gkey }, context: groupContext, scope_rows: gRows })).join(''))
      : '';
    const gfBand = (groupBy && layout.bands.group_footer?.visible)
      ? wrapBand('group_footer', gfEls.map(el => renderElement(el, { current: { [groupBy]: gkey }, context: groupContext, scope_rows: gRows })).join(''))
      : '';
    return ghBand + masterBlocks + gfBand;
  }).join('');

  // Tekli belge layout'larında (sales_quote, sales_invoice vb.) main band'ları tek master row'a bağlanır.
  // Çoklu master raporlarında (stok listesi vb.) ilk row kullanılır; ana içerik master/detail bantlarında zaten döner.
  const headerScopeCurrent = mainRows[0] || null;
  const reportHeader = wrapBand('report_header', elementsByBand('report_header').map(el => renderElement(el, { current: headerScopeCurrent, context, scope_rows: mainRows })).join(''));
  const pageHeader   = wrapBand('page_header',   elementsByBand('page_header').map(el => renderElement(el, { current: headerScopeCurrent, context, scope_rows: mainRows })).join(''));
  const pageFooter   = wrapBand('page_footer',   elementsByBand('page_footer').map(el => renderElement(el, { current: headerScopeCurrent, context, scope_rows: mainRows })).join(''));
  const reportFooter = wrapBand('report_footer', elementsByBand('report_footer').map(el => renderElement(el, { current: headerScopeCurrent, context, scope_rows: mainRows })).join(''));

  // Barkod elementi varsa JsBarcode lib'i CDN'den yüklenip tüm SVG'leri renderle
  const hasBarcode = layout.elements.some(e => e.type === 'barcode');
  const barcodeScript = hasBarcode
    ? `<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
       <script>
         function renderBarcodes() {
           if (typeof JsBarcode === 'undefined') return setTimeout(renderBarcodes, 100);
           document.querySelectorAll('svg[data-bc-value]').forEach(function(svg){
             try {
               JsBarcode(svg, svg.getAttribute('data-bc-value') || ' ', {
                 format: svg.getAttribute('data-bc-format') || 'CODE128',
                 displayValue: svg.getAttribute('data-bc-text') !== 'false',
                 width: 1, height: 40, margin: 0, fontSize: 12,
               });
             } catch(e) { svg.outerHTML = '<div style="color:red;font-size:9px">'+e.message+'</div>'; }
           });
         }
         renderBarcodes();
       </script>`
    : '';

  return `<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><title>${escape(name || 'Rapor')}</title>
<style>
  @page { size: ${layout.paper_size} ${layout.page_orientation}; margin: ${layout.margin_top}mm ${layout.margin_right}mm ${layout.margin_bottom}mm ${layout.margin_left}mm; }
  body { margin: 0; font-family: ${layout.font_family || 'Arial, sans-serif'}; font-size: ${layout.font_size || 10}pt; }
  .rpt-band { width: 100%; }
  .rpt-band-master, .rpt-band-detail { page-break-inside: avoid; }
  img, svg { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style></head><body>
${reportHeader}
${pageHeader}
${groupBlocks}
${reportFooter}
${pageFooter}
${barcodeScript}
<script>
  // Barkod yüklemesi sonrası yazdırma — yoksa hemen
  (function(){
    function go(){ window.focus(); window.print(); }
    if (${hasBarcode ? 'true' : 'false'}) setTimeout(go, 800); else window.onload = go;
  })();
</script>
</body></html>`;
}

function formatNumber(v, format) {
  if (v == null) return '';
  if (format === 'integer') return Math.round(v).toString();
  if (format === 'percent') return (v.toFixed(2)) + '%';
  if (format === 'currency') return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  // varsayılan
  if (Number.isInteger(v)) return v.toLocaleString('tr-TR');
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}
